import React from "react"
import "twin.macro"
import { Typography } from "../../components/Typography"
import { Separator } from "../../components/Separator"
import { IconButton } from "../../components/IconButton"
import { BlurBG } from "../../components/BlurBG"
import {
  useSimulationDataSelector,
  SimulationData,
} from "../SimulationData.context"
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons"

const PlanetDate = ({
  title,
  children,
}: React.PropsWithChildren<{ title: string }>) => (
  <div tw="w-full">
    <div tw="flex justify-between w-full z-10">
      <Typography
        renderAs="span"
        variant="small"
        tw="self-baseline text-gray-medium"
      >
        {title}
      </Typography>{" "}
      <Typography renderAs="span" variant="body" tw="self-baseline">
        {children}
      </Typography>
    </div>
    <Separator tw="transform -translate-y-1 z-0" />
  </div>
)

const PlanetName = ({ selectedIndex }: { selectedIndex: number }) => {
  const name = useSimulationDataSelector(
    simulationData =>
      (simulationData.dots.length > 0 &&
        simulationData.dots[selectedIndex]?.name) ||
      "N/A"
  )

  return <Typography variant="h3">{name}</Typography>
}

const PlanetDataHead = ({
  onClickRight,
  onClickLeft,
  selectedIndex,
}: React.PropsWithChildren<{
  onClickRight: () => void
  onClickLeft: () => void
  selectedIndex: number
}>) => {
  const leftButton = React.useMemo(
    () => <IconButton icon={faChevronLeft} onClick={onClickLeft} />,
    [onClickLeft]
  )

  const rightButton = React.useMemo(
    () => <IconButton icon={faChevronRight} onClick={onClickRight} />,
    [onClickRight]
  )

  return (
    <>
      {leftButton}
      <PlanetName selectedIndex={selectedIndex} />
      {rightButton}
    </>
  )
}

const PlanetDataTable = ({ selectedIndex }: { selectedIndex: number }) => {
  const speed = useSimulationDataSelector(
    (simulationData: SimulationData) =>
      (simulationData.dots.length > 0 &&
        simulationData.dots[selectedIndex]?.speed.toFixed()) ||
      "N/A"
  )

  const mass = useSimulationDataSelector(
    (simulationData: SimulationData) =>
      (simulationData.dots.length > 0 &&
        simulationData.dots[selectedIndex]?.mass.toFixed()) ||
      "N/A"
  )

  const isAlive = useSimulationDataSelector(
    (simulationData: SimulationData) =>
      (simulationData.dots.length > 0 &&
        simulationData.dots[selectedIndex]?.isAlive.toString()) ||
      "N/A"
  )

  const x = useSimulationDataSelector(
    (simulationData: SimulationData) =>
      (simulationData.dots.length > 0 &&
        simulationData.dots[selectedIndex]?.x.toFixed()) ||
      "N/A"
  )

  const y = useSimulationDataSelector(
    (simulationData: SimulationData) =>
      (simulationData.dots.length > 0 &&
        simulationData.dots[selectedIndex]?.y.toFixed()) ||
      "N/A"
  )

  return (
    <>
      <PlanetDate title="mass">{mass}</PlanetDate>
      <PlanetDate title="velocity">{speed}</PlanetDate>
      <PlanetDate title="alive">{isAlive}</PlanetDate>
      <PlanetDate title="coord">
        {x} / {y}
      </PlanetDate>
    </>
  )
}

export const PlanetData = ({
  nextButtonClicked,
  previousButtonClicked,
}: {
  nextButtonClicked: () => void
  previousButtonClicked: () => void
}) => {
  const selectedIndex = useSimulationDataSelector(data => data.selectedIndex)

  return (
    <BlurBG tw="absolute right-0 -mr-4 p-4 w-52">
      <div tw="w-full flex justify-between items-center mb-4">
        <PlanetDataHead
          onClickLeft={previousButtonClicked}
          onClickRight={nextButtonClicked}
          selectedIndex={selectedIndex}
        />
      </div>
      <PlanetDataTable selectedIndex={selectedIndex} />
    </BlurBG>
  )
}
